const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

function getToken() {
  return localStorage.getItem('adminToken') || sessionStorage.getItem('adminToken');
}

function authHeaders({ json = false } = {}) {
  const token = getToken();
  const headers = {};
  if (token) headers.Authorization = `Bearer ${token}`;
  if (json) headers['Content-Type'] = 'application/json';
  return headers;
}

export function parseApiError(payload, fallback) {
  if (!payload || typeof payload !== 'object') return fallback;
  return (
    payload.message ||
    payload.error?.message ||
    (Array.isArray(payload.errors) && payload.errors[0]?.message) ||
    fallback
  );
}

/**
 * Normalizes one advisory record from various possible API shapes.
 * Keeps a single view-model for the UI without hardcoding backend-only assumptions.
 */
export function normalizeAdvisor(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const id = raw.id ?? raw.advisor_id;
  const name =
    raw.name ??
    raw.full_name ??
    raw.display_name ??
    '';
  const profileImageUrl =
    raw.profile_image_url ??
    raw.profileImageUrl ??
    raw.image_url ??
    raw.avatar_url ??
    raw.photo_url ??
    '';
  const linkedinUrl = raw.linkedin_url ?? raw.linkedinUrl ?? '';
  const displayOrder = Number(raw.display_order ?? raw.displayOrder ?? 0);
  const isActive =
    raw.is_active !== undefined
      ? Boolean(raw.is_active)
      : raw.active !== undefined
        ? Boolean(raw.active)
        : raw.status === 'active' || raw.status === undefined;
  const deletedAt = raw.deleted_at ?? raw.deletedAt ?? null;
  const categoryId = raw.category_id ?? raw.categoryId ?? null;
  const categoryName = raw.category_name ?? raw.categoryName ?? null;
  const categorySlug = raw.category_slug ?? raw.categorySlug ?? null;
  const createdAt = raw.created_at ?? raw.createdAt ?? null;
  const updatedAt = raw.updated_at ?? raw.updatedAt ?? null;

  return {
    id,
    name: String(name),
    designation: String(raw.designation ?? ''),
    company: String(raw.company ?? raw.organization ?? ''),
    qualification: String(raw.qualification ?? ''),
    bio: String(raw.bio ?? ''),
    profile_image_url: String(profileImageUrl),
    linkedin_url: String(linkedinUrl),
    display_order: Number.isFinite(displayOrder) ? displayOrder : 0,
    is_active: isActive,
    deleted_at: deletedAt,
    category_id: categoryId,
    category_name: categoryName,
    category_slug: categorySlug,
    created_at: createdAt,
    updated_at: updatedAt,
  };
}

export function extractAdvisoryList(json) {
  const d = json?.data;
  if (Array.isArray(d)) return d;
  if (Array.isArray(d?.advisors)) return d.advisors;
  if (Array.isArray(d?.items)) return d.items;
  if (Array.isArray(d?.records)) return d.records;
  if (Array.isArray(json?.advisors)) return json.advisors;
  return [];
}

export async function listAdvisories() {
  const res = await fetch(`${API_BASE_URL}/api/v1/admin/advisory`, {
    method: 'GET',
    headers: authHeaders({ json: false }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(parseApiError(json, 'Failed to load advisors'));
    err.status = res.status;
    throw err;
  }
  const data = json?.success ? json.data : json;
  return extractAdvisoryList({ data }).map(normalizeAdvisor).filter(Boolean);
}

export async function createAdvisory(body) {
  const res = await fetch(`${API_BASE_URL}/api/v1/admin/advisory`, {
    method: 'POST',
    headers: authHeaders({ json: true }),
    body: JSON.stringify(body),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(parseApiError(json, 'Failed to create advisor'));
    err.status = res.status;
    throw err;
  }
  return json?.success ? json.data : json;
}

export async function updateAdvisory(id, body) {
  const res = await fetch(`${API_BASE_URL}/api/v1/admin/advisory/${id}`, {
    method: 'PATCH',
    headers: authHeaders({ json: true }),
    body: JSON.stringify(body),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(parseApiError(json, 'Failed to update advisor'));
    err.status = res.status;
    throw err;
  }
  return json?.success ? json.data : json;
}

export async function toggleAdvisory(id, is_active) {
  const res = await fetch(`${API_BASE_URL}/api/v1/admin/advisory/${id}/toggle`, {
    method: 'PATCH',
    headers: authHeaders({ json: true }),
    body: JSON.stringify({ is_active }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(parseApiError(json, 'Failed to update status'));
    err.status = res.status;
    throw err;
  }
  return json?.success ? json.data : json;
}

export async function deleteAdvisory(id) {
  const res = await fetch(`${API_BASE_URL}/api/v1/admin/advisory/${id}`, {
    method: 'DELETE',
    headers: authHeaders({ json: false }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(parseApiError(json, 'Failed to delete advisor'));
    err.status = res.status;
    throw err;
  }
  return json?.success ? json.data : json;
}
